import {
  HabitSchedule,
  ScheduleEntryState,
  ScheduleTargetEntry,
} from '@griply/types';
import { getResolvedScheduleDays, isBetween } from '@griply/utils';
import { addWeeks, eachDayOfInterval } from 'date-fns';
import { useMemo } from 'react';

import { Options } from './use-timeframe-completions';
import { useTimeframeOccurrences } from './use-timeframe-occurrences';

export const useTimeframeEntries = (
  schedules: HabitSchedule[],
  { startDate, endDate }: Options,
): {
  dateEntries: ScheduleTargetEntry[];
  timeframeDateEntries: ScheduleTargetEntry[];
} => {
  // get a bigger range for extra completions to display in the month-view
  const { rangeStartDate, rangeEndDate } = useMemo(
    () => ({
      rangeStartDate: addWeeks(startDate, -5),
      rangeEndDate: addWeeks(endDate, 5),
    }),
    [endDate, startDate],
  );
  const { occurrences } = useTimeframeOccurrences(schedules, {
    startDate: rangeStartDate,
    endDate: rangeEndDate,
  });

  return useMemo(() => {
    const daysBetween = eachDayOfInterval({
      start: rangeStartDate,
      end: rangeEndDate,
    });

    const targetEntries = getResolvedScheduleDays({
      schedules,
      days: daysBetween,
      occurrences,
      fallbackEndDate: rangeEndDate,
    }).map<ScheduleTargetEntry>((day) => ({
      date: day.date,
      id: day.entryId ?? `${day.date.getTime()}`,
      state: day.state ?? ScheduleEntryState.Complete,
      value: day.value,
      hidden: day.hidden ?? undefined,
      target: day.target,
      onDateManual: day.onDateManual,
      showValueAsBadge: (day.value ?? 0) > 1,
    }));

    return {
      dateEntries: targetEntries.filter(({ date }) =>
        isBetween(date, rangeStartDate, rangeEndDate),
      ),
      timeframeDateEntries: targetEntries.filter(({ date }) =>
        isBetween(date, startDate, endDate),
      ),
    };
  }, [
    endDate,
    occurrences,
    rangeEndDate,
    rangeStartDate,
    schedules,
    startDate,
  ]);
};
